<template>

  <div>

    <!-- Rate settings constants -->
    <h4 style="margin: 12px 0 8px; font-size: 16px; font-weight: 500;">Rate settings</h4>

    <!-- Info banner -->
    <el-alert
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom: 16px;">
      <template #default>
        These apply to every tier below. Tier-specific values (Tenor Range, Rate #1–#N, MW Rate, RRU Rate, RRU Set) live inside each tier card.
      </template>
    </el-alert>

    <el-form inline label-position="top">

      <select-sharer label="Sharer" v-model="row.organisation_id" :disabled="disabled" />
      <text-input label="Name" v-model="row.name" :disabled="disabled" />
      <check-box label="First Sharer Only" v-model="row.firstSharerOnly" :disabled="disabled" />

      <!-- XOL-319: single mutually-exclusive choice replaces the two independent
           "Rate By Sharer No / Qty" toggles (which allowed ambiguous both-on/both-off). -->
      <el-form-item>
        <template #label><span>Rate billing <help-tip :content="rateBySharerHelp" /></span></template>
        <el-radio-group v-model="sharerRateMode" :disabled="disabled">
          <el-radio-button label="none">None</el-radio-button>
          <el-radio-button label="no">By Sharer No</el-radio-button>
          <el-radio-button label="qty">By Sharer Qty</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <!-- XOL-462: strict billing rules — own visual group so the two switches
           don't wrap orphaned beside the radio group above. -->
      <div class="rate-settings__strict">
        <div class="rate-settings__strict-title">Strict billing rules</div>

        <el-form-item>
          <template #label><span>Strict term <help-tip :content="strictTermHelp" /></span></template>
          <el-switch
            v-model="row.strictTerm"
            :disabled="disabled"
            active-text="On"
            inactive-text="Off"
            inactive-color="#DCDFE6"
            aria-label="Strict term" />
        </el-form-item>

        <el-form-item>
          <template #label><span>{{ strictPositionLabel }} <help-tip :content="strictPositionHelp" /></span></template>
          <el-switch
            v-model="row.strictPosition"
            :disabled="disabled"
            active-text="On"
            inactive-text="Off"
            inactive-color="#DCDFE6"
            :aria-label="strictPositionLabel" />
        </el-form-item>
      </div>

      <el-row :gutter="guts">

        <!-- XOL-464: Min is v-if'd out entirely when Strict position is off,
             reversing XOL-462 §3.1's "disabled, not hidden" decision (Carlos,
             2026-08-26 — see the Decisions log). v-if is safe on this specific
             item: this <el-form> has no :model, no ref and no prop on any
             item (§2.4), and validate() is never called, so the XOL-451
             v-if/v-else-reused-prop trap (which makes el-form validate()
             silently always pass) cannot apply here. -->
        <el-col v-if="row.strictPosition" :xs="24" :sm="24" :md="8" :lg="6">
          <number-input :label="fromSharerLabel" :min="1" :max="10" v-model="row.fromSharer" :disabled="disabled" />
          <div v-if="fromSharerError" style="color:#f56c6c; font-size:12px; margin-top:4px;">{{ fromSharerError }}</div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <number-input :label="toSharerLabel" :min="1" :max="10" v-model="row.toSharer" :disabled="disabled" />
          <div v-if="toSharerError" style="color:#f56c6c; font-size:12px; margin-top:4px;">{{ toSharerError }}</div>
        </el-col>

        <el-col :xs="24" :sm="24" :md="8" :lg="6">
          <number-input label="Height (metres)" :min="0" :max="121" v-model="row.height" :disabled="disabled" />
        </el-col>

      </el-row>

    </el-form>

    <hr style="border: 0; border-top: 1px solid #ebeef5; margin: 16px 0;" />

    <!-- Tier editor -->
    <sharer-rate-tiers
      ref="tiersEditor"
      v-model="row.tiers"
      :disabled="disabled"
      :to-sharer="row.toSharer"
      :from-sharer="effectiveFromSharer"
      :validation-errors="tierValidationErrors">
    </sharer-rate-tiers>

  </div>

</template>



<script>

import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'

import SelectSharer from '@/components/atoms/selects/SelectSharer'
import SharerRateTiers from './tiers/SharerRateTiers'

export default {

  mixins: [NumberMixins, PostMixins],

  components: {
    SelectSharer,
    SharerRateTiers
  },

  props: {
    rowData: {
      type: Object,
      default: null
    }
  },

  computed: {
    disabled () {
      return !this.isManager('finance|management|admin')
    },

    // XOL-319: bridge the single radio choice to the two underlying booleans.
    // Exactly one of ratesBySharerNo / ratesBySharerQty can be true (or neither),
    // so both-on / both-off ambiguity is no longer expressible.
    sharerRateMode: {
      get () {
        if (this.row.ratesBySharerNo) return 'no'
        if (this.row.ratesBySharerQty) return 'qty'
        return 'none'
      },
      set (val) {
        this.row.ratesBySharerNo = (val === 'no')
        this.row.ratesBySharerQty = (val === 'qty')
      }
    },

    // XOL-376/XOL-462: label the shared unit per the active mode. The
    // payload keys stay toSharer/fromSharer regardless of the displayed
    // label. Neutral 'Sharers' for the unflagged mode (settled with the API
    // agent) — None falls back to the byNo-like billing path server-side,
    // but 'Sharer Count' would wrongly imply the mode is set.
    sharerUnitLabel () {
      if (this.row.ratesBySharerNo) return 'Sharer Count'
      if (this.row.ratesBySharerQty) return 'Sharer Qty'
      return 'Sharers'
    },

    // XOL-464: the 'Max ' prefix only earns its keep when Min is also on
    // screen (row.strictPosition true) — with no lower bound to contrast
    // against, there is no "max" to distinguish it from, so the label reverts
    // to the plain mode-driven noun (the pre-XOL-462 label).
    toSharerLabel () {
      return (this.row.strictPosition ? 'Max ' : '') + this.sharerUnitLabel
    },

    fromSharerLabel () {
      return 'Min ' + this.sharerUnitLabel
    },

    // XOL-464: single source of truth for "which sub-Min positions are hidden
    // and out of scope" — feeds both the tier-completeness loop (validateTiers,
    // below) and the tier editor's rendered Rate #N range (threaded down as
    // :from-sharer). Falls back to 1 (i.e. nothing hidden) whenever the header
    // itself is not in a state where a lower bound makes sense — strictPosition
    // off, an out-of-range fromSharer, or fromSharer > toSharer. The last two
    // guards are load-bearing for usability, not just defence: number-input
    // emits on every keystroke, so editing Min from 2 to 5 against Max 4 passes
    // through an inverted band on the way. Without the fallback the whole
    // Rate # grid would empty and re-populate mid-typing.
    effectiveFromSharer () {
      if (!this.row.strictPosition) return 1
      var f = Number(this.row.fromSharer)
      if (isNaN(f) || f < 1 || f > 10) return 1
      var t = Number(this.row.toSharer)
      if (!isNaN(t) && t >= 1 && f > t) return 1
      return f
    },

    // XOL-464: fixed, mode-independent label — replaces the old three-branch
    // 'Strict No' / 'Strict Qty' / 'Strict Position' computed (XOL-462 §3.2).
    // The flag governs both the No and Qty axis, so a mode-driven label
    // wrongly implied it applied to only one (Carlos, 2026-08-26 — see the
    // Decisions log). Kept as a one-line computed (not a data field) so it
    // stays alongside the other label computeds it replaced, even though it
    // no longer branches on `row`.
    strictPositionLabel () {
      return 'Strict Billing Pos'
    },

    // XOL-462 §6.2 — exact tip copy, one string across all three modes.
    rateBySharerHelp () {
      return 'Picks what decides which Rate # column a sharer is billed from — By Sharer No uses the sharer\'s own number, By Sharer Qty uses how many sharers are on the site. None leaves the rate unflagged and billing falls back to the sharer\'s number.'
    },

    // XOL-464: added a clause per the XOL-463 author (PR #242) — strict term
    // is enforced against "has the term actually lapsed", not the projection
    // horizon, so flipping it on mid-term produces no visible change on the
    // fee schedule/timeline until the term ends. Without warning for that, a
    // user who toggles it on mid-term and sees nothing change has no way to
    // tell whether it saved, took effect, or is broken.
    strictTermHelp () {
      return 'Off, the last tier\'s rate keeps billing indefinitely after that tier ends. On, billing stops at the end of the last tier and nothing is charged after that — those months still appear on the finance screens, flagged as billing errors. If the term hasn\'t ended yet, turning this on won\'t change anything you see right away — the effect only shows once the term is reached.'
    },

    // XOL-464: reworded the 'Off,' clause — it used to lean on a Min–Max
    // range that is no longer visible on screen when the flag is off (Min is
    // now v-if'd away, not just disabled). The 'On,' clause is unchanged.
    strictPositionHelp () {
      return 'Off, a sharer is billed regardless of position — no minimum or maximum applies. On, a sharer outside the Min–Max range is not billed at all and is flagged as an error.'
    }
  },

  methods: {
    setTierErrors (errors) {
      this.tierValidationErrors = errors
    },

    // XOL-462 §2.2: `row` is seeded from getTableStruct('rates') — the LEGACY
    // `rates` table, not `sharer_rates`. `strictTerm` / `strictPosition` are
    // absent entirely on a new-rate object, so a bare assignment would bind to
    // nothing (Vue 2 non-reactive-add trap): the switches would render
    // permanently off and un-clickable, with no error anywhere. $set all four
    // header keys — harmless for fromSharer/toSharer, which the legacy table
    // already carries, and load-bearing for the two strict flags. Must run
    // BEFORE normalizeTiers() / anything else reads these keys.
    // XOL-464: strictPosition is $set BEFORE fromSharer — fromSharer's own
    // default now reads it. A rate saved by the currently-shipped SPA can carry
    // a stale, positive fromSharer with strictPosition off (XOL-462 §3.1's
    // "disabled, not reset" design); once fromSharer also drives Min's
    // visibility and the tier-completeness loop's lower bound, that stale value
    // must not silently reappear as a live lower bound the moment the row is
    // reopened — it only becomes live again if strictPosition itself is on.
    normalizeHeader () {
      var r = this.row
      this.$set(r, 'strictPosition', !!r.strictPosition)
      this.$set(r, 'fromSharer', (r.strictPosition && Number(r.fromSharer) > 0) ? Number(r.fromSharer) : 1)
      this.$set(r, 'toSharer', Number(r.toSharer) > 0 ? Number(r.toSharer) : 10)
      this.$set(r, 'strictTerm', !!r.strictTerm)
    },

    normalizeTiers () {
      if (this.row.tiers && Array.isArray(this.row.tiers) && this.row.tiers.length > 0) {
        // Tiers present — coerce numbers, sort by seqNo, re-stamp seqNo
        var normalized = this.row.tiers
          .slice()
          .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
          .map(function (t, i) {
            return {
              seqNo: i + 1,
              fromMonth: Number(t.fromMonth) || 0,
              toMonth: Number(t.toMonth) || 0,
              telco1: Number(t.telco1) || 0,
              telco2: Number(t.telco2) || 0,
              telco3: Number(t.telco3) || 0,
              telco4: Number(t.telco4) || 0,
              telco5: Number(t.telco5) || 0,
              telco6: Number(t.telco6) || 0,
              telco7: Number(t.telco7) || 0,
              telco8: Number(t.telco8) || 0,
              telco9: Number(t.telco9) || 0,
              telco10: Number(t.telco10) || 0,
              mwRate: Number(t.mwRate) || 0,
              rruRate: Number(t.rruRate) || 0,
              rruSet: Number(t.rruSet) || 0
            }
          })
        this.$set(this.row, 'tiers', normalized)
      } else if (this.row.id) {
        // Existing row with flat columns — synthesize single Tier 1
        var flatTier = {
          seqNo: 1,
          fromMonth: Number(this.row.fromMonth) || 0,
          toMonth: Number(this.row.toMonth) || 0,
          telco1: Number(this.row.telco1) || 0,
          telco2: Number(this.row.telco2) || 0,
          telco3: Number(this.row.telco3) || 0,
          telco4: Number(this.row.telco4) || 0,
          telco5: Number(this.row.telco5) || 0,
          telco6: Number(this.row.telco6) || 0,
          telco7: Number(this.row.telco7) || 0,
          telco8: Number(this.row.telco8) || 0,
          telco9: Number(this.row.telco9) || 0,
          telco10: Number(this.row.telco10) || 0,
          mwRate: Number(this.row.mwRate) || 0,
          rruRate: Number(this.row.rruRate) || 0,
          rruSet: Number(this.row.rruSet) || 0
        }
        this.$set(this.row, 'tiers', [flatTier])
      } else {
        // New record — one blank Tier 1 at month 0
        var blankTier = {
          seqNo: 1,
          fromMonth: 0,
          toMonth: 12,
          telco1: 0,
          telco2: 0,
          telco3: 0,
          telco4: 0,
          telco5: 0,
          telco6: 0,
          telco7: 0,
          telco8: 0,
          telco9: 0,
          telco10: 0,
          mwRate: 0,
          rruRate: 0,
          rruSet: 0
        }
        this.$set(this.row, 'tiers', [blankTier])
      }
    },

    // Called in created() for new records only: try to pre-fill tiers from
    // the tenant's Default Tier Template. Silent on failure — the pristine
    // blank Tier 1 is already set by normalizeTiers().
    onTemplateSeed (data) {
      // Only seed if we still have the pristine single all-zero Tier 1
      // (guard against clobbering any edits or existing rows).
      if (this.row.id) return
      if (!this.row.tiers || this.row.tiers.length !== 1) return
      var t = this.row.tiers[0]
      var isPristine = (
        t.seqNo === 1 &&
        t.fromMonth === 0 &&
        t.toMonth === 12 &&
        t.telco1 === 0 && t.telco2 === 0 && t.telco3 === 0 && t.telco4 === 0 &&
        t.telco5 === 0 && t.telco6 === 0 && t.telco7 === 0 && t.telco8 === 0 &&
        t.telco9 === 0 && t.telco10 === 0 &&
        t.mwRate === 0 && t.rruRate === 0 && t.rruSet === 0
      )
      if (!isPristine) return

      if (!data || !data.tiers || !Array.isArray(data.tiers) || data.tiers.length === 0) {
        // data:null — no template saved; keep the pristine blank Tier 1
        return
      }

      // Map template tiers into the edit dialog: null → 0 (non-template edit
      // path represents blank as 0; keeps seeded dialog consistent with a
      // manually-created rate and avoids nullable rates in PR-A tests).
      var seeded = data.tiers
        .slice()
        .sort(function (a, b) { return (a.seqNo || 0) - (b.seqNo || 0) })
        .map(function (t, i) {
          return {
            seqNo: i + 1,
            fromMonth: Number(t.fromMonth) || 0,
            toMonth: Number(t.toMonth) || 0,
            telco1: Number(t.telco1) || 0,
            telco2: Number(t.telco2) || 0,
            telco3: Number(t.telco3) || 0,
            telco4: Number(t.telco4) || 0,
            telco5: Number(t.telco5) || 0,
            telco6: Number(t.telco6) || 0,
            telco7: Number(t.telco7) || 0,
            telco8: Number(t.telco8) || 0,
            telco9: Number(t.telco9) || 0,
            telco10: Number(t.telco10) || 0,
            mwRate: Number(t.mwRate) || 0,
            rruRate: Number(t.rruRate) || 0,
            rruSet: Number(t.rruSet) || 0
          }
        })
      this.$set(this.row, 'tiers', seeded)
    },

    // Called by RatesAction before save to validate tiers.
    // Returns { valid, errors } where errors is an array of per-tier error objects.
    // Delegates the boolean to the child SharerRateTiers.validate() when mounted,
    // so the authoritative isValid computed lives in one place.
    validateTiers () {
      var tiers = this.row.tiers
      var errors = []

      if (!tiers || tiers.length === 0) {
        return { valid: false, errors: errors }
      }

      // XOL-376 (R1): every tier must have Rate #1..toSharer > 0. Compute the
      // completeness limit once from row.toSharer.
      var n = Number(this.row.toSharer)
      var toSharerValid = !isNaN(n) && n >= 1 && n <= 10
      this.toSharerError = toSharerValid ? null : this.toSharerLabel + ' must be between 1 and 10.'
      var limit = toSharerValid ? Math.min(n, 10) : 0

      // XOL-462 §6.5: fromSharer (Min) range 1..10 and fromSharer <= toSharer are
      // enforced ONLY when strictPosition is on — when off the field is disabled
      // and the API ignores it (§7), so blocking a save on a stale value would be
      // a dead end the user cannot clear.
      var f = Number(this.row.fromSharer)
      var fromSharerValid = true
      this.fromSharerError = null
      if (this.row.strictPosition) {
        if (isNaN(f) || f < 1 || f > 10) {
          fromSharerValid = false
          this.fromSharerError = this.fromSharerLabel + ' must be between 1 and 10.'
        } else if (toSharerValid && f > n) {
          fromSharerValid = false
          this.fromSharerError = this.fromSharerLabel + ' cannot be greater than ' + this.toSharerLabel + '.'
        }
      }

      // XOL-462 §2.3: must be ANDed into BOTH branches below, including the
      // $refs.tiersEditor branch — the branch the real app takes, and one the
      // child SharerRateTiers knows nothing about (its prop contract is
      // deliberately not widened; the parent owns header errors).
      var headerValid = toSharerValid && fromSharerValid

      // Build per-tier error map (needed for field-level display regardless of
      // whether the child ref is available)
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i]
        var tierError = {}

        // Tier 1 must start at month 0
        if (i === 0 && t.fromMonth !== 0) {
          tierError.fromMonth = 'Tier 1 must start at month 0.'
        }

        // toMonth > fromMonth
        if (t.toMonth <= t.fromMonth) {
          tierError.range = 'Tier end must be greater than tier start (month ' + t.fromMonth + ').'
        }

        // Rate #effectiveFromSharer..toSharer must all be > 0 (completeness).
        // XOL-464: narrowed from a fixed 1..limit — positions below
        // effectiveFromSharer are hidden (not rendered) whenever Strict
        // position is on with Min >= 2, so requiring them would name a Rate #
        // the user cannot see or fill in. The invariant this loop preserves:
        // the enumerated positions are always a SUBSET of the rendered
        // positions (SharerRateTierCard's ratePositions, threaded the same
        // effectiveFromSharer down as :from-sharer).
        var missing = []
        for (var p = this.effectiveFromSharer; p <= limit; p++) {
          if (!(Number(t['telco' + p]) > 0)) missing.push(p)
        }
        if (missing.length > 0) {
          tierError.completeness = 'Rate #' + missing.join(', #') + ' required (must be greater than 0).'
        }

        errors.push(tierError)
      }

      // Delegate the authoritative boolean to the child's validate() (which uses
      // the reactive isValid computed — includes contiguity). Fall back to local
      // check when the child ref is not yet mounted (e.g. unit tests).
      var valid
      if (this.$refs && this.$refs.tiersEditor && typeof this.$refs.tiersEditor.validate === 'function') {
        valid = this.$refs.tiersEditor.validate() && headerValid
      } else {
        // Local fallback: field-level errors + contiguity + header range checks
        var hasFieldErrors = errors.some(function (e) { return Object.keys(e).length > 0 })
        var contiguous = true
        for (var k = 1; k < tiers.length; k++) {
          if (tiers[k].fromMonth !== tiers[k - 1].toMonth) { contiguous = false; break }
        }
        valid = !hasFieldErrors && contiguous && headerValid
      }

      return { valid: valid, errors: errors }
    }
  },

  // XOL-462 (found by exercising the built form in a browser, before review):
  // fromSharerError is only ever CLEARED inside
  // validateTiers(), so an error set while Strict position was on survives a
  // later off-toggle until the next save attempt. Left alone, the Min column
  // renders the muted "Not used…" hint stacked directly above a stale
  // "…must be…" / "…cannot be greater than…" error enforcing a rule the
  // system has just stopped applying — self-contradictory, and the assertion
  // in §6.5 that the two "never stack" only holds if you never toggle
  // strictPosition off while an error is set. Clear the error at the source
  // (not just hide it in the template) so nothing stale can leak into a
  // later render.
  //
  // XOL-464: also reset row.fromSharer to 1 on the same off-toggle. This
  // reverses the XOL-462 Decisions-log entry "hiding Min does NOT reset
  // row.fromSharer" — that decision rested on fromSharer being INERT while
  // strictPosition is off. It no longer is: fromSharer now also determines
  // which sub-Min tier positions are hidden and which are required
  // (effectiveFromSharer, above), so a stale Min left in place would
  // immediately widen the completeness rule the instant Strict position is
  // switched back on, re-surfacing a stored 0.00 at a now-billable position
  // as a blocking validation error rather than as a silent, confident free
  // bill — without the reset, SharerTierResolver::resolve() would otherwise
  // return 0.0 (not null) for that position and the recompute job would take
  // the ordinary write path, setting currentRate = 0.00 with no audit chip
  // and no banner anywhere.
  watch: {
    'row.strictPosition': function (val) {
      if (!val) {
        this.fromSharerError = null
        this.row.fromSharer = 1
      }
    }
  },

  data () {
    return {
      guts: 12,
      row: this.rowData ? this.rowData : this.getTableStruct('rates'),
      tierValidationErrors: [],
      toSharerError: null,
      fromSharerError: null
    }
  },

  created () {
    this.normalizeHeader()
    this.normalizeTiers()
    // For new records only: try to pre-seed tiers from the Default Tier Template.
    // Silent on GET failure — the pristine blank Tier 1 is already in place.
    // Existing rows (row.id set) are never affected.
    if (!this.row || !this.row.id) {
      this.fetchData('/rates/sharer/default-tiers', this.onTemplateSeed)
    }
  }

}
</script>

<style scoped>
/* XOL-462 §6.3: block-level wrapper forces a line break inside the inline
   form (el-form-item is display:inline-block) without a heavy <hr>. Element
   2.11 applies inline-form styling via the descendant selector
   `.el-form--inline .el-form-item`, which still matches through this wrapper. */
.rate-settings__strict {
  display: block;
  margin-top: 8px;
}

.rate-settings__strict-title {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
</style>
